import { useEffect, useState, useMemo, useRef } from "react";
import { axInst } from "../../../Components/Login/Service";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Loader } from "rsuite";
import WaterMark from "../../../Components/WaterMark/WaterMark";

// API Calls
const fetchServiceData = async (body) => {
  try {
    const res = await axInst.post(
      "dealer-review/dealer_service_dashboard",
      body,
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Service API Error:", error);
    return [];
  }
};

const fetchDivisionDropdown = async () => {
  try {
    const res = await axInst.post("dealer-review/divison_filter");
    return res.data?.distinct_divisions?.buckets ?? [];
  } catch (error) {
    console.error("Dropdown API Error:", error);
    return [];
  }
};

const ServiceTablePdf = ({ show }) => {
  const [selectedOption, setSelectedOption] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempSelectedOption, setTempSelectedOption] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const { data = [], submitted } = useSelector((e) => e.filtersDealerReview);

  useEffect(() => {
    const fetchData = async () => {
      const divisions = await fetchDivisionDropdown();
      setDivisionOptions(divisions);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const queryBody = useMemo(
    () => ({
      ...data,
      division_name: selectedOption.length ? selectedOption : [],
    }),
    [data, selectedOption],
  );

  const {
    data: serviceData = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["service", submitted, selectedOption],
    queryFn: () => fetchServiceData(queryBody),
    cacheTime: 0,
    staleTime: 0,
  });

  const months = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  // Dynamically extract all available fiscal years
  const years = useMemo(() => {
    const yearSet = new Set();
    serviceData.forEach((item) => {
      months.forEach((month) => {
        if (item[month]) {
          Object.keys(item[month]).forEach((year) => {
            yearSet.add(year); // Add dynamic fiscal years
          });
        }
      });
    });
    return Array.from(yearSet).sort(); // Sort dynamically extracted years
  }, [serviceData]);

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    setTempSelectedOption((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const clearAll = () => {
    setTempSelectedOption([]);
  };

  const handleApplyFilters = () => {
    setSelectedOption(tempSelectedOption);
    setDropdownOpen(false); // optional: close dropdown on apply
  };
  useEffect(() => {
    if (dropdownOpen) {
      setTempSelectedOption(selectedOption);
    }
  }, [dropdownOpen]);
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDivisionOptions = divisionOptions.filter((option) =>
    option.key.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  if (isFetching)
    return (
      <div className="grid items-center justify-center w-full h-full">
        <Loader />
      </div>
    );
  if (isError) return <div>Error loading data</div>;

  return (
    <div className="bg-white  relative">
      <WaterMark />

      {/* Header & Dropdown */}
      <div className="flex justify-between items-center p-1 pb-[2px]">
        <header className="text-lg font-bold text-gray-800">Services</header>
        {show && (
          <div className="my-1 flex justify-end">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="border border-gray-300 text-[12px] bg-white rounded-md w-48 p-0.5 flex items-center justify-between px-2"
              >
                {selectedOption.length > 0
                  ? ` Selected Divisions(${selectedOption.length})`
                  : "Select Divisions"}
                <span
                  className={`ml-2 transform transition-transform text-xl ${
                    dropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <MdOutlineKeyboardArrowDown />
                </span>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                  ref={dropdownRef}
                >
                  <div className="p-2">
                    <input
                      type="text"
                      value={searchTerm}
                      name="search-division"
                      onChange={handleSearchChange}
                      placeholder="Search Division..."
                      className="w-full p-1 border border-gray-300 rounded mb-1"
                    />
                    <div className="flex justify-end px-2 ">
                      <button
                        className="text-blue-600 hover:text-blue-800 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAll();
                        }}
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {filteredDivisionOptions.map((option, index) => (
                        <label key={index} className="block text-sm">
                          <input
                            type="checkbox"
                            value={option.key}
                            checked={tempSelectedOption.includes(option.key)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          {option.key}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-white  border-t border-gray-200 text-xs flex  items-center gap-2">
                    <button
                      className="px-3 py-1 bg-[#2A5775] w-full text-white "
                      onClick={(e) => {
                        handleApplyFilters();
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="drilltable w-full max-w-[1200px] ">
        {serviceData.length === 0 && (
          <p className="text-center text-gray-600 py-4">Data Not Available</p>
        )}

        <table
          className="w-full min-w-max border-collapse border border-gray-300 pl-2 pr-8 mb-[10px]"
          // style={{ direction: "ltr" }} // Keeps table content left-aligned
        >
          {serviceData.length > 0 && (
            <thead>
              <tr>
                <th
                  className="  p-1 bg-[#D4E1EC] sticky left-0 z-10"
                  rowSpan={2}
                ></th>
                {months.map((month) => (
                  <th
                    key={month}
                    colSpan={years.length}
                    className="border p-1 bg-[#28526E] text-white text-xs"
                  >
                    {month}
                  </th>
                ))}
                <th
                  colSpan={years.length}
                  className="border p-1 bg-[#28526E] text-white text-xs"
                >
                  Total
                </th>
              </tr>
              <tr>
                {months.flatMap((month) =>
                  years.map((year) => (
                    <th
                      key={`${month}_${year}`}
                      className="bg-[#D4E1EC] text-xs"
                    >
                      {year}
                    </th>
                  )),
                )}
                {years.map((year) => (
                  <th key={`total_${year}`} className="bg-[#D4E1EC] text-xs">
                    {year}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {serviceData.length > 0 && (
            <tbody>
              {serviceData.map((item, idx) => {
                const isPercentageRow = String(item.id).includes("%");

                const totalsByYear = years.reduce((acc, year) => {
                  acc[year] = months.reduce((sum, month) => {
                    const val = parseFloat(item[month]?.[year]) || 0;
                    return sum + val;
                  }, 0);

                  if (idx === 2) {
                    acc[year] = acc[year] / 12;
                  }
                  acc[year] = parseFloat(acc[year].toFixed(2));
                  return acc;
                }, {});

                return (
                  <tr key={idx}>
                    <td className="border p-1 text-xs bg-[#D4E1EC] text-center sticky left-0 z-10">
                      {item.id}
                    </td>

                    {/* Month-wise values */}
                    {months.flatMap((month) =>
                      years.map((year) => {
                        const val = item[month]?.[year];
                        return (
                          <td
                            key={`${month}_${year}_${idx}`}
                            className="border p-1 text-xs text-center"
                          >
                            {val !== undefined
                              ? isPercentageRow
                                ? `${val}%`
                                : val
                              : "-"}
                          </td>
                        );
                      }),
                    )}

                    {/* Totals */}
                    {years.map((year) => (
                      <td
                        key={`total_${year}_${idx}`}
                        className="border p-1 text-xs text-center"
                      >
                        {isPercentageRow
                          ? `${totalsByYear[year]}%`
                          : totalsByYear[year] || 0}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default ServiceTablePdf;
